// =====================================================
// VISOR DE IMÁGENES SATELITALES POR LOTE
// Adaptación de VisorImagenesSatelitales para un polígono específico
// src/pages/Agro/Establecimiento/VisorImagenesSatelitalesLote.js
// =====================================================

import React, { useState, useEffect } from "react"
import {
  Card,
  CardBody,
  Row,
  Col,
  Button,
  ButtonGroup,
  Spinner,
  Badge,
  Input,
  Label,
  FormGroup,
  Alert,
} from "reactstrap"
import { format, subDays, subMonths } from "date-fns"
import { es } from "date-fns/locale"

import { getSerieImagenes, getUrlImagenSatelital } from "api/agro"
import { getErrorMessage } from "helpers/error_helpers"
import "leaflet/dist/leaflet.css"

const VisorImagenesSatelitalesLote = ({ idEstablecimiento, poligono }) => {
  const [fechaSeleccionada, setFechaSeleccionada] = useState(
    format(subDays(new Date(), 5), "yyyy-MM-dd"),
  )
  const [tipoVisualizacion, setTipoVisualizacion] = useState("TRUE-COLOR")
  const [serieImagenes, setSerieImagenes] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [modoComparacion, setModoComparacion] = useState(false)
  const [imagenUrl, setImagenUrl] = useState(null)

  useEffect(() => {
    if (idEstablecimiento && poligono?.id && !modoComparacion) {
      cargarImagen()
    }
  }, [
    idEstablecimiento,
    poligono?.id,
    fechaSeleccionada,
    tipoVisualizacion,
    modoComparacion,
  ])

  const cargarImagen = async () => {
    if (!poligono?.id || modoComparacion) return

    setLoading(true)
    setError(null)
    setImagenUrl(null)
    try {
      const data = await getUrlImagenSatelital(
        idEstablecimiento,
        poligono.id,
        fechaSeleccionada,
        tipoVisualizacion,
      )
      setImagenUrl(data.url)
    } catch (err) {
      console.error("Error obteniendo URL de imagen satelital:", err)
      // BDSJEA-460: se muestra el message del backend (p. ej. lote sin geometría), no un texto genérico
      setError(
        getErrorMessage(err, {
          fallback: "No se pudo obtener la imagen satelital.",
        }),
      )
      setLoading(false)
    }
  }

  const cargarSerie = async () => {
    if (!poligono?.id) return

    setLoading(true)
    setError(null)

    try {
      const hasta = format(new Date(), "yyyy-MM-dd")
      const desde = format(subMonths(new Date(), 3), "yyyy-MM-dd")

      const data = await getSerieImagenes(
        idEstablecimiento,
        poligono.id,
        desde,
        hasta,
        tipoVisualizacion,
        15, // cada 15 días
      )
      setSerieImagenes(data || [])
      setModoComparacion(true)
    } catch (err) {
      setError(
        getErrorMessage(err, { fallback: "Error cargando serie de imágenes" }),
      )
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">
            <i className="mdi mdi-satellite-variant me-2" />
            Imágenes Satelitales -{" "}
            {poligono?.nombreLote || `Lote ${poligono?.id}`}
          </h5>
          <div className="d-flex align-items-center gap-2">
            <Badge color="info">Sentinel-2</Badge>
            <Button
              color="light"
              size="sm"
              onClick={() => {
                setModoComparacion(false)
                setFechaSeleccionada(
                  format(subDays(new Date(), 5), "yyyy-MM-dd"),
                )
                setTipoVisualizacion("TRUE-COLOR")
                cargarImagen()
              }}
            >
              <i className="mdi mdi-map-marker-radius me-1" />
              Ver lote
            </Button>
          </div>
        </div>

        {/* Controles */}
        <Row className="mb-3">
          <Col md={4}>
            <FormGroup>
              <Label size="sm">Fecha</Label>
              <Input
                type="date"
                bsSize="sm"
                value={fechaSeleccionada}
                onChange={e => setFechaSeleccionada(e.target.value)}
                max={format(new Date(), "yyyy-MM-dd")}
                disabled={modoComparacion}
              />
            </FormGroup>
          </Col>

          <Col md={5}>
            <FormGroup>
              <Label size="sm">Visualización</Label>
              <ButtonGroup size="sm" className="d-block">
                <Button
                  color={
                    tipoVisualizacion === "TRUE-COLOR"
                      ? "primary"
                      : "outline-primary"
                  }
                  onClick={() => setTipoVisualizacion("TRUE-COLOR")}
                  disabled={modoComparacion}
                >
                  <i className="mdi mdi-image me-1" />
                  Color Real
                </Button>
                <Button
                  color={
                    tipoVisualizacion === "NDVI" ? "success" : "outline-success"
                  }
                  onClick={() => setTipoVisualizacion("NDVI")}
                  disabled={modoComparacion}
                >
                  <i className="mdi mdi-leaf me-1" />
                  NDVI
                </Button>
                <Button
                  color={
                    tipoVisualizacion === "MOISTURE" ? "info" : "outline-info"
                  }
                  onClick={() => setTipoVisualizacion("MOISTURE")}
                  disabled={modoComparacion}
                >
                  <i className="mdi mdi-water me-1" />
                  Humedad
                </Button>
              </ButtonGroup>
            </FormGroup>
          </Col>

          <Col md={3} className="d-flex align-items-end gap-1">
            {!modoComparacion ? (
              <Button
                color="secondary"
                size="sm"
                onClick={cargarSerie}
                disabled={loading}
              >
                <i className="mdi mdi-timeline me-1" />
                Comparar fechas
              </Button>
            ) : (
              <Button
                color="outline-secondary"
                size="sm"
                onClick={() => {
                  setModoComparacion(false)
                  cargarImagen()
                }}
              >
                <i className="mdi mdi-close me-1" />
                Cerrar comparación
              </Button>
            )}
          </Col>
        </Row>

        {error && (
          <Alert color="warning" className="mb-3">
            <i className="mdi mdi-alert-circle me-2" />
            {error}
          </Alert>
        )}

        {/* Imagen principal */}
        {!modoComparacion ? (
          <div
            className="text-center position-relative"
            style={{ minHeight: 400 }}
          >
            {imagenUrl ? (
              <>
                <img
                  src={imagenUrl}
                  alt={`Imagen satelital - ${tipoVisualizacion}`}
                  style={{
                    maxWidth: "100%",
                    maxHeight: 500,
                    border: "2px solid #dee2e6",
                    borderRadius: 8,
                  }}
                  onLoad={() => {
                    console.log("Imagen satelital cargada correctamente")
                    setLoading(false)
                    setError(null)
                  }}
                  onError={() => {
                    setLoading(false)
                    setError(
                      "No se pudo cargar la imagen. Puede que no haya datos disponibles.",
                    )
                  }}
                />
                <div className="mt-2 text-muted">
                  <small>
                    <i className="mdi mdi-calendar me-1" />
                    {format(new Date(fechaSeleccionada), "dd/MM/yyyy", {
                      locale: es,
                    })}
                  </small>
                </div>
              </>
            ) : loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: 400 }}
              >
                <Spinner color="primary" />
                <span className="ms-2">Cargando imagen satelital...</span>
              </div>
            ) : (
              <div
                className="text-muted d-flex flex-column justify-content-center align-items-center"
                style={{ height: 400 }}
              >
                <i
                  className="mdi mdi-satellite-variant"
                  style={{ fontSize: 64, opacity: 0.3 }}
                />
                <p className="mt-3">
                  Seleccione una fecha para ver la imagen satelital
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Modo comparación - Serie temporal */
          <div>
            <h6 className="mb-3">
              <i className="mdi mdi-timeline me-2" />
              Evolución temporal - Últimos 3 meses
            </h6>
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : serieImagenes.length > 0 ? (
              <Row>
                {serieImagenes.map((img, idx) => (
                  <Col key={idx} md={3} sm={6} className="mb-3">
                    <Card className="h-100">
                      <img
                        src={
                          img.urlImagen || img.urlThumbnail || img.urlTrueColor
                        }
                        alt={`Imagen ${img.fecha}`}
                        style={{
                          width: "100%",
                          height: 150,
                          objectFit: "cover",
                        }}
                        onError={e => {
                          e.target.src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150'%3E%3Crect fill='%23f0f0f0' width='200' height='150'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' fill='%23999'%3ESin datos%3C/text%3E%3C/svg%3E"
                        }}
                      />
                      <CardBody className="p-2 text-center">
                        <small className="text-muted">
                          {format(new Date(img.fecha), "dd MMM yyyy", {
                            locale: es,
                          })}
                        </small>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>
            ) : (
              <Alert color="info">
                No hay imágenes disponibles para el período.
              </Alert>
            )}
          </div>
        )}

        {/* Leyendas */}
        {tipoVisualizacion === "NDVI" && !modoComparacion && (
          <div
            className="mt-3 p-2 bg-light rounded font-size-14"
            data-testid="leyenda-ndvi"
          >
            <span className="text-muted d-block mb-1">Leyenda NDVI:</span>
            <div className="d-flex flex-wrap justify-content-between gap-2">
              <span style={{ color: "#d73027" }}>■■ Suelo/Estrés</span>
              <span style={{ color: "#fc8d59" }}>■■ Vegetación escasa</span>
              <span style={{ color: "#fee08b" }}>■■ Moderada</span>
              <span style={{ color: "#91cf60" }}>■■ Densa</span>
              <span style={{ color: "#1a9850" }}>■■ Muy densa</span>
            </div>
          </div>
        )}

        <div
          className="mt-3 text-muted font-size-14"
          data-testid="fuente-satelital"
        >
          <i className="mdi mdi-information-outline me-1" />
          Imágenes de Sentinel-2 (ESA/Copernicus). Resolución: 10m/pixel.
          Actualización: cada 5 días.
        </div>
      </CardBody>
    </Card>
  )
}

export default VisorImagenesSatelitalesLote
